import { useCallback, useEffect, useState } from "react";
import usePresetRtcService from "ui/preset/service";
import { CameraType } from "webrtc-player/common/base";
import { showToast } from "ui/components/common";
import PresetItem from "ui/preset/preset-item";
import type { PresetType } from "ui/preset/service";
import { Modal } from "antd";
import clsx from "clsx";
import { SvgAlertPrevPage, SvgAlertNextPage } from "ui/svgs";
import { savePresetAlgorithm } from "pages/dashboard/virtualGuard/service";

type propsType = {
  cameraMac: string;
  orgId: number;
  nvrId: string | number;
  devId: string;
  cameraId: string | number;
  remoteIndex: string | number;
  capabilities?: any;
};

const PresetList = ({
  selectedCamera,
  row = 2,
  col = 2,
  pageSize = 4,
  fixedSize = true,
}: {
  selectedCamera: propsType;
  row?: number;
  col?: number;
  pageSize?: number;
  fixedSize?: boolean; // just fixed size for 4k in focus page
}) => {
  const PageSize = pageSize;
  const [presetList, setPresetList] = useState<PresetType[]>([]);
  const { gotoPreset, getPresetList, setPreset, deletePreset } =
    usePresetRtcService();
  const [loading, setLoading] = useState(true);
  const [presetPage, setPresetPage] = useState(0);
  const camera: CameraType = {
    id: +selectedCamera.cameraId,
    macAddress: selectedCamera.cameraMac,
    remoteIndex: +selectedCamera.remoteIndex,
  };

  const updatePresetList = useCallback(() => {
    console.log("updatePresetList", +selectedCamera.nvrId);
    getPresetList(
      +selectedCamera.orgId,
      +selectedCamera.nvrId,
      selectedCamera?.devId,
      camera,
    )
      .then((res) => {
        setLoading(false);
        console.log("presetList res", res);
        setPresetList(res.presetList);
      })
      .catch((err: Error) => {
        showToast(
          1,
          "There was a problem getting the preset list. Please try again. Error:" +
            err.message,
        );
        throw err;
      });
    setLoading(true);
  }, [selectedCamera]);

  useEffect(() => {
    if (selectedCamera?.capabilities?.ptz === false) return;
    updatePresetList();
  }, [selectedCamera]);

  const handleSetPreset = async (id: number) => {
    try {
      setLoading(true);
      await setPreset(
        +selectedCamera.orgId,
        +selectedCamera.nvrId,
        selectedCamera?.devId,
        camera,
        id,
        `Preset ${id}`,
      );
      updatePresetList();
    } catch (error) {
      showToast(
        1,
        "There was a problem setting the preset. Please try again. Error:" +
          error?.message,
      );
    }
    setLoading(false);
  };

  const handlePosPreset = async (id: number) => {
    try {
      await gotoPreset(
        +selectedCamera.orgId,
        +selectedCamera.nvrId,
        selectedCamera?.devId,
        camera,
        id,
      );
    } catch (error) {
      showToast(
        1,
        "There was a problem positioning the preset. Please try again. Error:" +
          error?.message,
      );
    }
  };

  async function handleDeletePreset(id: number) {
    Modal.confirm({
      title: "Do you want to delete this preset?",
      content: `Deleting this preset will also remove its associated Algorithm and Schedule under Virtual Guard. This action cannot be undone.`,
      centered: true,
      icon: null,
      width: 500,
      async onOk() {
        try {
          setLoading(true);
          const res = await savePresetAlgorithm(
            selectedCamera.cameraId,
            id,
            [],
            [],
            null,
            false,
          );
          if (res.status !== 200)
            throw new Error("Failed to delete preset algorithm");
          await deletePreset(
            selectedCamera.orgId,
            +selectedCamera.nvrId,
            selectedCamera?.devId,
            camera,
            id,
          );
          //updatePresetList();
          if (Math.ceil((presetList.length - 1) / PageSize) < presetPage + 1) {
            setPresetPage(Math.max(presetPage - 1, 0));
          }
          setPresetList((prev) => {
            return prev.filter((preset) => preset.id !== id);
          });
        } catch (error) {
          showToast(
            1,
            "There was a problem deleting the preset. Please try again. Error:" +
              error?.message,
          );
        }
        setLoading(false);
      },
      okText: "Delete",
      okButtonProps: {
        className: "primaryButton dangerFill",
      },
      cancelButtonProps: {
        className: "primaryButton bordered",
      },
    });
  }

  const handlePreviousAlert = () => {
    setPresetPage(presetPage - 1);
  };
  const handleNextAlert = () => {
    setPresetPage(presetPage + 1);
  };

  function getEmptyPresetIndex(presetList) {
    for (let i = 1; i <= 255; i++) {
      const match = presetList?.find((preset) => preset.id === i);
      if (!match) return i;
    }
  }

  return (
    <div className="flex items-center justify-center gap-1 pt-2 pb-2 h-full">
      {!loading && presetList?.length !== 0 && (
        <>
          <button
            className="w-[32px] h-[32px] rounded-full flex items-center justify-center  hover:bg-zinc-200 disabled:hover:bg-transparent fill-neutral-800 disabled:opacity-25"
            disabled={presetPage == 0 || loading}
            onClick={handlePreviousAlert}
          >
            <SvgAlertPrevPage width="14" />
          </button>
          <div
            className={`grid gap-2 grid-cols-${col} grid-rows-${row} h-full max-h-full max-w-full`}
          >
            {presetList
              ?.slice(presetPage * PageSize, (presetPage + 1) * PageSize)
              .map((preset) => (
                <div className={"w-full max-w-100 max-h-100 overflow-hidden"}>
                  <div
                    className={
                      "h-full aspect-video object-cover rounded-lg overflow-hidden max-w-full"
                    }
                  >
                    <PresetItem
                      homePreset={preset?.id === presetList[0]?.id}
                      key={preset.id}
                      selectedCamera={selectedCamera}
                      data={preset}
                      onPos={() => handlePosPreset(preset.id)}
                      onDelete={() => handleDeletePreset(preset.id)}
                    />
                  </div>
                </div>
              ))}
            {/* {new Array(
          (presetPage + 1) * PageSize -
            (presetList.length - presetPage * PageSize),
        )
          ?.fill(0)
          .map((_, index) => {
            return (
              <div className="w-[113px] h-[64px] 3xl:w-[226px] 3xl:h-[128px]">
                <PresetItem
                  key={index}
                  selectedCamera={selectedCamera}
                  onSet={() => handleSetPreset(getEmptyPresetIndex(presetList))}
                />
              </div>
            );
          })} */}
          </div>
          <button
            className="w-[32px] h-[32px] rounded-full flex items-center justify-center hover:bg-zinc-200 disabled:hover:bg-transparent fill-neutral-800 disabled:opacity-25"
            disabled={presetList.length / PageSize - 1 <= presetPage || loading}
            onClick={handleNextAlert}
          >
            <SvgAlertNextPage width="14" />
          </button>
        </>
      )}
      {!loading && presetList?.length === 0 && (
        <div style={{ textAlign: "center" }}>No preset available</div>
      )}
      {loading && (
        <div className="w-full h-full bg-black absolute top-0 left-0 bg-opacity-20 rounded-2xl flex items-center justify-center">
          <div
            className=" text-blue-500  inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="status"
          ></div>
        </div>
      )}
    </div>
  );
};

export default PresetList;
